import { QueryParameter } from './../../../interfaces/queryString';
import { SapiEntity } from './../../../services/sapi.service';
import { MediaclipService } from './../../../services/mediaclip.service';
import { Observable, combineLatest } from 'rxjs';
import { TableRow } from './../../../services/bb-table.service';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractListComponent } from '../../shared/abstract-list.component';
import { FilterSetService } from '../../../services/filter-set.service';
import { CustomField } from '../../../interfaces/publication';
import { BbTableService, BbTableSelectOption } from '../../../services/bb-table.service';
import { ModalDefinition, DialogDefinition } from '../../../services/overlay.service';
import { Mediaclip } from '../../../interfaces/mediaclip';
import { WorklistService } from '../../../services/worklist.service';
import { HostListener } from '@angular/core';
import { LocalStorageService } from 'angular-2-local-storage';
import { WebSocketsService } from '../../../services/web-sockets.service';
import { TranscodingService, MediaclipJob } from '../../../services/transcoding.service';

@Component({
	selector: 'mediaclip-list',
	templateUrl: './mediaclip-list.component.html',
	styleUrls: ['./mediaclip-list.component.scss'],
	providers: [BbTableService, FilterSetService],
})

export class MediaclipListComponent extends AbstractListComponent implements OnInit {
	@ViewChild('deleteTemplate') deleteTemplate: TemplateRef<any>;
	relationInfo: any = {};
	playlistPanelOpen: boolean;
	putRight: boolean;
	deleteRight: boolean;
	hasDraftSelected: boolean;
	requestStatus: any;
	shownRows: TableRow[];
	playlistClickToClose: boolean;
	allowCustomPreset = true;
	rowsContainLiveClips = false;

	rootPermission: boolean;

	constructor(
		protected bbTableService: BbTableService,
		protected filterSetService: FilterSetService,
		protected localStorageService: LocalStorageService,
		protected worklistService: WorklistService,
		protected webSocketsService: WebSocketsService,
		protected transcodingService: TranscodingService,
		private mediaclipService: MediaclipService
	) {

		super(bbTableService, filterSetService);

		this.listConfiguration = {
			name: 'mediaclip',
			basePath: '/library/mediaclip/',
			activePath: '/library/mediaclips',
			entityTranslation: this.translate.instant('MEDIACLIP').toLowerCase(),
			entitiesTranslation: this.translate.instant('MEDIACLIPS').toLowerCase()
		};

		if (this.localStorageService.get('ovp-mediaclip-list-entity')) {
			this.listConfiguration.name = this.localStorageService.get('ovp-mediaclip-list-entity');
		}

		this.filterConfig = {
			search: true,
			filters: this.mediaclipService.getFilterConfigOptions(),
			presets: [
				{
					label: this.translate.instant('ALL'),
					value: 'all',
					filterSet: [],
				},
				{
					label: this.translate.instant('PUBLISHED'),
					value: 'published',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'status',
							operator: 'is',
							value: 'published'
						}]
					}],
				},
				{
					label: this.translate.instant('DRAFT'),
					value: 'draft',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'status',
							operator: 'is',
							value: 'draft'
						}]
					}],
				},
				{
					label: this.translate.instant('VIDEO'),
					value: 'video',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'mediatype',
							operator: 'is',
							value: 'video'
						}]
					}],
				},
				{
					label: this.translate.instant('AUDIO'),
					value: 'audio',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'mediatype',
							operator: 'is',
							value: 'audio'
						}]
					}],
				},
				{
					label: this.translate.instant('ON_DEMAND'),
					value: 'on_demand',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'sourcetype',
							operator: 'is',
							value: 'on_demand'
						}]
					}],
				},
				{
					label: this.translate.instant('LIVE'),
					value: 'live',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'sourcetype',
							operator: 'is',
							value: 'live'
						}]
					}],
				},
				{
					label: this.translate.instant('EMPTY'),
					value: 'isEmpty',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'isEmpty',
							operator: 'is',
							value: 'true'
						}]
					}],
				}
			]
		};

		this.subscriptions.add(this.authenticationService.getAllowedCustomFields().subscribe((fields: CustomField[]) => {
			fields.forEach((field: CustomField) => {

				// map custom field type to filter type
				let filterType: string;

				switch (field.itype) {
					case 'text':
						filterType = 'text';
						break;
					case 'number':
						filterType = 'number';
						break;
					case 'date':
						filterType = 'date';
						break;
					case 'checkbox':
						filterType = 'boolean';
						break;
					case 'list':
					case 'string':
						filterType = 'select';
						break;
				}

				// for now, "color" and "relation" are ignored

				if (filterType) {
					this.filterConfig.filters.push({
						property: field.name,
						label: field.label,
						type: filterType
					});
				}
			});
		}));

		this.getCustomPresets();

	}

	@HostListener('document:click', ['$event'])
	onMouseUp(ev: Event) {
		if (this.playlistPanelOpen && this.playlistClickToClose) {
			this.togglePlaylistPanel();
		}
	}

	ngOnInit() {
		this.bbTableService
			.setSelect(BbTableSelectOption.multi)
			.setLimit(15)
			.sort('createddate', 'desc')
			.linkToUrl();

		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
		this.authenticationService.hasPermission('delete').subscribe(hasRight => this.deleteRight = hasRight);

		this.authenticationService.hasPermission('root').subscribe(hasRight => this.rootPermission = hasRight);

		super.ngOnInit();

		this.subscriptions.add(combineLatest([this.bbTableService.rowsRetrievedObservable, this.transcodingService.jobsSubject])
			.subscribe((res: any[]) => {
				const mediaclipJobs: MediaclipJob[] = res[1];
				const mediaclipJobIdsArray = mediaclipJobs.map(mediaClipJob => mediaClipJob && mediaClipJob.id);
				for (let i = 0; i < this.rows.length; i++) {
					const row = this.rows[i];
					if (row.data.mediatype === 'video' && !row.data.transcodingFinished) {
						if (mediaclipJobIdsArray.indexOf(this.rows[i].id) !== -1) {  // check if displayed mediaclips have jobs
							const foundMediaclipJob = mediaclipJobs.find(clipJob => clipJob && clipJob.id === this.rows[i].id);
							const average = this.transcodingService.getAverage(foundMediaclipJob.mediajobs);
							this.rows[i].transcodingAverage = average;
						}
					}
				}
			})
		);
	}

	set useCommonClip(value: boolean) {
		if (value) {
			this.listConfiguration.name = 'commonclip';
		} else {
			this.listConfiguration.name = 'mediaclip';
		}
		this.localStorageService.set('ovp-mediaclip-list-entity', this.listConfiguration.name);
		this.filterSetService.setEntity(this.listConfiguration.name);
		this.bbTableService.setEntity(this.listConfiguration.name);
		this.bbTableService.getRows(true);
	}

	get useCommonClip() {
		return this.listConfiguration.name === 'commonclip';
	}

	setPlaylistClosing(bool: boolean) {
		this.playlistClickToClose = bool;
	}

	rowsRetrieved(rows: TableRow[]) {
		super.rowsRetrieved(rows);

		this.rowsContainLiveClips = false;
		for (const row of rows) {
			if (!this.rowsContainLiveClips) {
				if (row.data.sourceid && row.data.sourceid.startsWith('live_api_')) {
					this.rowsContainLiveClips = true;
					break;
				}
			}
		}
	}

	rowsSelected(rows: TableRow[]) {
		super.rowsSelected(rows);
		this.hasDraftSelected = false;
		for (const row of rows) {
			if (row.data.status === 'draft') {
				this.hasDraftSelected = true;
			}
		}
	}

	// override super deleteSingle
	deleteSingle(entity: SapiEntity, dialogContents?: string | TemplateRef<any>) {
		if (!dialogContents) {
			dialogContents = this.translate.instant('DELETE_ENTITY_CONFIRM', { entity: this.entityTranslation });
		}
		const relations = this.relationInfo.relations = this.mediaclipService.getRelations(entity);
		this.relationInfo.defaultContents = dialogContents;
		this.relationInfo.projects = null;
		if (relations?.project?.length) {
			const queryParams: QueryParameter[] = [{
				name: 'q',
				values: [
					'id:"' + relations.project.join(' OR id:"') + '"'
				]
			}];
			this.sapiService.query('project', queryParams).subscribe((projects) => {
				this.relationInfo.projects = projects.items;
			});
		}
		const deleteObs = super.deleteSingle(entity, this.deleteTemplate);
		return deleteObs;
	}

	// override super doubleclick redirect:
	redirectToDetails(row: TableRow) {
		if (row && row.data && row.data.id) {
			this.router.navigate(['library/mediaclip', row.data.id, 'metadata']);
		}
	}

	showPreview(clip: Mediaclip) {

		const modal: ModalDefinition = {
			icon: 'film',
			title: (clip.title || clip.originalfilename),
			component: {
				name: 'embed-preview',
				inputs: {
					mediaclip: clip
				}
			}
		};

		this.overlayService.createModal(modal);
	}

	// direct to the method that can perform the actual function
	executeRequest(req: string) {
		const items = this.selectedRows;
		const clipIdList = new Array();

		// only process if we actually have an item and the dialog is accepted
		if (!req || !items) {
			return false;
		}

		// execute for every selected item
		items.forEach(
			item => {
				clipIdList.push({ id: item.id, sourceid: (item.data && item.data.sourceid) || null });
			}
		);

		// determine the action we need to execute and set the actionCycle to false on fail
		switch (req) {
			case 'delete':
				this.overlayService.setLoading(true);
				if (items.length === 1) {
					this.deleteClip(items[0].data);
				} else {
					this.batchAction('delete', clipIdList);
				}
				this.overlayService.setLoading(false);
				break;
			case 'togglePublished':
				// Only set to loading, the table will refresh and remove the loading state
				this.overlayService.setLoading(true);
				if (items.length === 1) {
					this.changeStatus(items[0].data);
				} else {
					this.batchAction('set', clipIdList);
				}
				break;
			case 'addToWorklist':
				if (items.length === 1) {
					this.addToWorklistBatch([items[0].data]);
				} else if (items.length === 0) {
					this.batchAction('addToWorklist', this.rows);
				} else {
					this.batchAction('addToWorklist', items);
				}
				break;
		}
	}

	addToWorklistBatch(items: any) {
		const amount = this.worklistService.addItems(items);
		let msg = null;

		if (amount > 1) {
			msg = this.translate.instant('X_ITEMS_ADDED_TO_WORKLIST', { x: amount });
		} else if (amount === 1) {
			msg = this.translate.instant('ITEM_ADDED_TO_WORKLIST');
		} else {
			msg = this.translate.instant('NO_ITEM_ADDED_TO_WORKLIST');
		}
		this.overlayService.createToast(msg, 'info-circle');
	}

	// request the right dialog and executes the request on confirmation
	requestDirector(req: string) {
		const items = this.selectedRows;

		// require a valid request
		if (!req || !items) {
			return;
		}

		// define the dialog
		const dialog: DialogDefinition = {
			icon: null,
			title: null,
			contents: null,
			onConfirm: () => {
				this.executeRequest(req);
			}
		};

		// set dialog values per request
		switch (req) {
			case 'delete': // build a confirmation for delete actions
				dialog.icon = 'trash-o';
				if (items.length === 1) {
					const config = { entity: this.translate.instant('MEDIACLIP').toLowerCase(), id: items[0].id };
					dialog.title = this.translate.instant('DELETE_ENTITY', config);
					dialog.contents = this.translate.instant('DELETE_ENTITY_CONFIRM', config);
				} else {
					const config = { nr: items.length, entities: this.translate.instant('MEDIACLIPS').toLowerCase() };
					dialog.title = this.translate.instant('DELETE_SELECTED_ENTITIES', config);
					dialog.contents = this.translate.instant('DELETE_SELECTED_ENTITIES_CONFIRM', config);
				}
				this.overlayService.createDialog(dialog);
				break;
			default:
				this.executeRequest(req); // execute request
				break;
		}
	}

	// delete a single media clip
	deleteClip(row: Mediaclip) {
		if (!row) {
			return false;
		}

		if (row.sourceid && row.sourceid.startsWith('live_api_')) {
			const uuid = row.sourceid.split('live_api_')[1];

			this.liveApiService.deleteUrl('/stream/' + uuid).subscribe(
				(data) => {
					this.bbTableService.removeRows([row.id]);
					const msg = this.translate.instant('MEDIACLIP_HAS_SUCCESFULLY_BEEN_DELETED');
					this.overlayService.createToast(msg, 'info-circle');
				},
				(err) => {
					this.overlayService.createToast(this.translate.instant('ERROR', { items: row }), 'check');
				}
			);
		} else {
			const observable = this.sapiService.delete('mediaclip', row.id);
			observable.subscribe(
				(data) => {
					this.bbTableService.removeRows([row.id]);
					const msg = this.translate.instant('MEDIACLIP_HAS_SUCCESFULLY_BEEN_DELETED');
					this.overlayService.createToast(msg, 'info-circle');

				},
				(err) => {
					this.overlayService.createToast(this.translate.instant('ERROR', { items: row }), 'check');
				}
			);
		}
	}

	batchAction(action: string, payload: any[]) {

		if (!action || !payload || !payload.length) {
			return false;
		}
		let observable: Observable<any>;

		const clipList = payload.filter(element => !element.sourceid || !element.sourceid.startsWith('live_api_'));
		const idList = clipList.map(element => element.id);
		const bbStreamsList = payload.filter(element => element.sourceid && element.sourceid.startsWith('live_api_'));

		// determine what batch action to execute
		switch (action) {
			case 'delete':
				bbStreamsList.forEach((bbStream) => {
					const uuid = bbStream.sourceid.split('live_api_')[1];

					this.liveApiService.deleteUrl(`/stream/${uuid}`).subscribe(
						(data) => {
							this.bbTableService.removeRows([bbStream.id]);
						},
						(err) => {
							this.overlayService.createToast(this.translate.instant('ERROR', { items: bbStreamsList }), 'check');
						}
					);
				});

				observable = this.sapiService.deleteMultiple('mediaclip', idList);
				observable.subscribe(
					(data) => {
						this.bbTableService.removeRows(payload);
						const msg = this.translate.instant('X_MEDIACLIPS_HAVE_SUCCESFULLY_BEEN_DELETED', { x: payload.length });
						this.overlayService.createToast(msg, 'info-circle');
					},
					(err) => {
						this.overlayService.createToast(this.translate.instant('ERROR', { items: idList }), 'check');
					}
				);
				break;
			case 'set':
				// determine correct new status
				let updateValue = 'draft';
				if (this.hasDraftSelected) {
					updateValue = 'published';
				}

				// THIS METHOD ONLY WORKS WITH MEDIACLIPS AT THIS TIME
				observable = this.sapiService.updateMultiple('mediaclip', idList, 'status', updateValue);
				observable.subscribe(
					(data) => {
						this.bbTableService.getRows(true);
						const msg = this.translate.instant('MEDIACLIPS_ACTION_WITH_PAYLOAD_SUCCESSFUL', { items: idList });
						this.overlayService.createToast(msg, 'info-circle');
					},
					(err) => {
						this.overlayService.createToast(this.translate.instant('ERROR', { items: idList }), 'check');
					}
				);

				break;
			case 'addToWorklist':
				this.addToWorklistBatch(clipList);
				break;
		}
	}

	// change status of mediaclip
	changeStatus(row: Mediaclip) {

		// logic to toggle published state
		if (!row) {
			return false;
		}

		// pick the correct new status
		let newStatus: string;
		if (row.status === 'draft') {
			newStatus = 'published';
		} else {
			newStatus = 'draft';
		}

		// create a copy of the clip data
		const clip = this.sapiService.get('mediaclip', row.id);
		clip.subscribe(
			(data: Mediaclip) => {
				const dataNew: Mediaclip = data;
				dataNew.status = newStatus;
				const observable = this.sapiService.update('mediaclip', dataNew);
				observable.subscribe(
					(dat) => {
						this.bbTableService.setRowData(row.id, dataNew);
						this.rowsSelected(this.selectedRows);
						this.overlayService.setLoading(false);
						const msg = this.translate.instant('MEDIACLIP_ACTION_WITH_PAYLOAD_SUCCESSFUL');
						this.overlayService.createToast(msg, 'info-circle');
					},
					err => {
						this.overlayService.setLoading(false);
						this.overlayService.createToast(this.translate.instant('ERROR', { items: row }), 'check');
					}
				);
			});
	}

	// open or close panel with playlists
	togglePlaylistPanel() {
		this.playlistPanelOpen = !this.playlistPanelOpen;
	}
}
